import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

interface ComparisonData {
  name: string
  valueA: number
  valueB: number
}

interface ComparisonChartProps {
  data?: ComparisonData[]
  title?: string
  labelA?: string
  labelB?: string
  colorA?: string
  colorB?: string
}

const defaultData: ComparisonData[] = [
  { name: 'Campaign 1', valueA: 245, valueB: 189 },
  { name: 'Campaign 2', valueA: 198, valueB: 234 },
  { name: 'Campaign 3', valueA: 312, valueB: 278 },
  { name: 'Campaign 4', valueA: 167, valueB: 201 },
  { name: 'Campaign 5', valueA: 289, valueB: 256 },
]

export function ComparisonChart({
  data = defaultData,
  title = 'Campaign Comparison',
  labelA = 'Version A',
  labelB = 'Version B',
  colorA = '#6366f1',
  colorB = '#8b5cf6',
}: ComparisonChartProps) {
  const processedData = data.map((item) => ({
    ...item,
    difference: item.valueA - item.valueB,
    percentDiff: (((item.valueA - item.valueB) / item.valueB) * 100).toFixed(1),
  }))

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <p className="text-sm text-gray-500 mt-1">Compare performance between variations</p>
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={processedData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey="name" stroke="#9ca3af" fontSize={12} />
          <YAxis stroke="#9ca3af" fontSize={12} />
          <Tooltip
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const data = payload[0].payload
                return (
                  <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
                    <p className="font-semibold text-gray-900 mb-2">{data.name}</p>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between space-x-4">
                        <span className="text-sm text-gray-600">{labelA}:</span>
                        <span className="text-sm font-medium">{data.valueA}</span>
                      </div>
                      <div className="flex items-center justify-between space-x-4">
                        <span className="text-sm text-gray-600">{labelB}:</span>
                        <span className="text-sm font-medium">{data.valueB}</span>
                      </div>
                      <div className="pt-2 border-t border-gray-100">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Difference:</span>
                          <span
                            className={`text-sm font-medium ${data.difference > 0 ? 'text-green-600' : 'text-red-600'}`}
                          >
                            {data.difference > 0 ? '+' : ''}
                            {data.percentDiff}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              }
              return null
            }}
          />
          <Legend />
          <Bar dataKey="valueA" name={labelA} fill={colorA} radius={[4, 4, 0, 0]} />
          <Bar dataKey="valueB" name={labelB} fill={colorB} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>

      {/* Summary Statistics */}
      <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-200">
        <div>
          <p className="text-xs text-gray-500 mb-1">Total {labelA}</p>
          <p className="text-lg font-semibold text-gray-900">
            {processedData.reduce((sum, item) => sum + item.valueA, 0).toLocaleString()}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">Total {labelB}</p>
          <p className="text-lg font-semibold text-gray-900">
            {processedData.reduce((sum, item) => sum + item.valueB, 0).toLocaleString()}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-1">Avg Difference</p>
          <p className="text-lg font-semibold text-gray-900">
            {(
              processedData.reduce((sum, item) => sum + parseFloat(item.percentDiff), 0) /
              processedData.length
            ).toFixed(1)}
            %
          </p>
        </div>
      </div>
    </div>
  )
}
